import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, HelpCircle, CreditCard, MessageSquare, Shield, Settings, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const sections = [
  {
    icon: HelpCircle,
    title: 'GETTING STARTED',
    items: [
      { q: 'How do I create an account?', a: 'Click "Start Investigation" on the homepage and fill out the registration form with your email and a secure password. You\'ll get access to free-tier content right away.' },
      { q: 'How do I log in?', a: 'Click "Sign In" on the homepage or navigate to the login page. Enter your registered email and password.' },
      { q: 'I forgot my password.', a: 'On the login page, click "Forgot Password". Enter your email and you\'ll receive a secure reset link. Check your spam folder if it doesn\'t arrive within a few minutes.' },
    ],
  },
  {
    icon: CreditCard,
    title: 'PAYMENTS & SUBSCRIPTIONS',
    items: [
      { q: 'What payment methods are accepted?', a: 'We accept Stripe (credit/debit cards) and PayPal. Memberships are billed monthly and you can cancel any time.' },
      { q: 'Does my membership renew automatically?', a: 'Yes. Memberships renew automatically each month at the listed price until you cancel. You can cancel anytime from your Profile and you keep access until the end of the period you\'ve paid for.' },
      { q: 'How do I upgrade my subscription?', a: 'Go to the "Subscription Tiers" page, choose your desired plan, and complete payment through your preferred provider.' },
      { q: 'How do I cancel my membership?', a: '1) Click your avatar in the top-right and choose Profile. 2) Scroll down to the "Membership & Billing" box. 3) Click CANCEL MEMBERSHIP and confirm. (Choosing the Free tier on the Tiers page does NOT cancel billing.) Your membership stops renewing immediately and you keep access until the end of the period you\'ve already paid for. Need a hand? Email deadlinebydaria@gmail.com from your account email.' },
      { q: 'What is your refund policy?', a: 'Because content is digital and delivered immediately, payments are non-refundable, except in cases of billing errors or duplicate charges, which we\'ll correct within 5 business days. Email deadlinebydaria@gmail.com with your payment details.' },
      { q: 'What will the charge look like on my statement?', a: 'Card payments appear as DEADLINE BY DARIA. PayPal payments appear as a PayPal charge to Deadline by Daria.' },
      { q: 'I was charged but my tier didn\'t update.', a: 'Payment processing can occasionally take a few moments. Try logging out and back in. If the issue persists, email us at deadlinebydaria@gmail.com with your payment confirmation.' },
    ],
  },
  {
    icon: Shield,
    title: 'CONTENT & ACCESS',
    items: [
      { q: 'Why can\'t I see certain posts?', a: 'Some posts are exclusive to higher subscription tiers. Upgrade your plan to unlock premium fictional case files and murder-mystery films.' },
      { q: 'Is any of this real?', a: 'No. Everything on Deadline by Daria is staged, fictional detective-themed entertainment performed by consenting adults. See our Content Policy for details.' },
      { q: 'What is the Store?', a: 'The Store contains individual items (collections) available for one-time purchase, separate from your subscription. Browse the Store tab to see what\'s available.' },
    ],
  },
  {
    icon: MessageSquare,
    title: 'MESSAGING & COMMUNITY',
    items: [
      { q: 'How do I send a direct message?', a: 'Navigate to the Messages page from the navigation bar. You can start a conversation with the creator directly.' },
      { q: 'What are chat rooms?', a: 'Chat rooms are real-time group discussions. Some rooms are restricted to certain subscription tiers. Check the chat panel for available rooms.' },
    ],
  },
  {
    icon: Settings,
    title: 'ACCOUNT & SETTINGS',
    items: [
      { q: 'How do I update my profile?', a: 'Click on your avatar in the navigation bar and select "Profile" to update your display name, avatar, and notification preferences.' },
      { q: 'How do I manage email notifications?', a: 'In your Profile settings, you can toggle which email notifications you receive (new posts, messages, etc.).' },
    ],
  },
  {
    icon: Users,
    title: 'TROUBLESHOOTING',
    items: [
      { q: 'The page isn\'t loading properly.', a: 'Try a hard refresh (Ctrl+Shift+R or Cmd+Shift+R). Clear your browser cache if the issue continues. We recommend using the latest version of Chrome, Firefox, or Safari.' },
      { q: 'I\'m not receiving emails.', a: 'Check your spam/junk folder for emails from notifications@deadlinebydaria.com. Add this address to your contacts to prevent future filtering.' },
      { q: 'Something else is broken.', a: 'Contact us at deadlinebydaria@gmail.com with a description of the issue, your browser, and any error messages you see. We\'ll investigate promptly.' },
    ],
  },
];

export default function HelpCentrePage() {
  const navigate = useNavigate();

  return (
    <motion.div
      className="min-h-screen bg-black"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Button
          variant="ghost"
          className="mb-6 text-amber-500 hover:text-amber-400 hover:bg-amber-900/20"
          onClick={() => navigate(-1)}
          data-testid="help-centre-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="bg-gray-900/90 border-2 border-amber-900/30 rounded-lg p-6 md:p-10">
          <h1 className="text-3xl font-headline text-amber-500 tracking-wider mb-2" data-testid="help-centre-title">
            HELP CENTRE
          </h1>
          <p className="text-gray-500 font-typewriter text-sm mb-8">
            Your guide to navigating the case files
          </p>

          <div className="space-y-8" data-testid="help-centre-sections">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <section key={section.title} className="bg-amber-900/10 border border-amber-700/30 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <h2 className="text-lg font-headline text-amber-400 tracking-wider">{section.title}</h2>
                  </div>
                  <div className="space-y-4">
                    {section.items.map((item, idx) => (
                      <div key={`${section.title}-${idx}`}>
                        <p className="text-amber-300 font-typewriter text-sm font-semibold">{item.q}</p>
                        <p className="text-gray-400 font-typewriter text-sm mt-1 leading-relaxed">{item.a}</p>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>

          <div className="mt-10 pt-6 border-t border-amber-900/30 text-center">
            <p className="text-gray-400 font-typewriter text-sm mb-3">
              Still need help? Reach out directly.
            </p>
            <a
              href="mailto:deadlinebydaria@gmail.com"
              className="inline-block bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider px-6 py-2 rounded transition-colors"
              data-testid="help-centre-contact-btn"
            >
              CONTACT US
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
